import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase-config";
import {
  createUpgradeLine,
  deleteUpgradeLine,
  formatLastPaid,
  normalizeUpgradeLine,
  PUBLIC_UPGRADE_LINE_SCOPE,
  subscribeUpgradeLines,
  UPGRADE_LINE_ADMIN_UID,
  updateUpgradeLine,
} from "../services/upgradeLines";
import "./UpgradeLine.css";

const emptyForm = () => ({ number: "", noOfLines: "", lastPaid: "", remarks: "" });
const loadErrorMessage = "Upgrade lines could not be loaded. Check your connection and retry.";
const saveErrorMessage = "The upgrade line could not be saved. Your details are still here. Please try again.";
const unavailableMessage = "This entry is no longer available. Cancel Edit to return to adding entries.";
const deleteErrorMessage = "This entry could not be deleted. Please try again.";

const UpgradeLineForm = ({ userId, publicMode, tableOnly }) => {
  const fieldId = useId();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [success, setSuccess] = useState("");
  const [operation, setOperation] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [entries, setEntries] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const entriesRef = useRef(null);
  const formRef = useRef(null);
  const busy = Boolean(operation);
  const editingMissing = Boolean(editing && (editing.unavailable || (entries !== null && !entries.some(row => row.id === editing.id))));
  const editStatus = editingMissing ? unavailableMessage : editing && entries === null ? "Wait for your saved entries to load before saving changes." : "";
  const showEntries = useCallback(rows => {
    entriesRef.current = rows;
    setEntries(rows);
  }, []);
  const editingId = editing?.id;

  useEffect(() => {
    if (tableOnly && editingId) formRef.current?.elements.namedItem("number")?.focus();
  }, [tableOnly, editingId]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    let unsubscribe;
    showEntries(null);
    setLoadError("");
    if (userId) {
      try {
        unsubscribe = subscribeUpgradeLines(userId, rows => {
          if (!active) return;
          showEntries(rows.filter(row => row.userId === userId));
          setLoadError("");
        }, () => {
          if (!active) return;
          showEntries(null);
          setLoadError(loadErrorMessage);
        });
      } catch {
        setLoadError(loadErrorMessage);
      }
    }
    return () => {
      active = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [userId, attempt, showEntries]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
    setFieldErrors({});
    setSaveError("");
  };

  const beginEdit = entry => {
    if (inFlight.current) return;
    const current = entriesRef.current?.find(row => row.id === entry.id);
    if (!current) return;
    setEditing({ id: current.id, createdAt: current.createdAt });
    setForm({ number: current.number, noOfLines: String(current.noOfLines), lastPaid: formatLastPaid(current.lastPaidCents), remarks: current.remarks });
    setFieldErrors({});
    setSaveError("");
    setSuccess("");
    setDeleteTarget(null);
    setDeleteError("");
    formRef.current?.elements.namedItem("number")?.focus();
  };

  const cancelEdit = () => {
    if (inFlight.current) return;
    resetForm();
    setSuccess("");
  };

  const updateField = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setSaveError("");
    setSuccess("");
  };

  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || !userId || (tableOnly && !editing)) return;
    if (editing && (editing.unavailable || !entriesRef.current?.some(row => row.id === editing.id))) {
      setSaveError(entriesRef.current === null ? "Wait for your saved entries to load before saving changes." : unavailableMessage);
      return;
    }
    setSaveError("");
    setSuccess("");
    try {
      normalizeUpgradeLine(form);
    } catch (failure) {
      setFieldErrors(failure.fieldErrors || {});
      if (!failure.fieldErrors) setSaveError(failure.message || "Check your details and try again.");
      if (failure.field) event.currentTarget.elements.namedItem(failure.field)?.focus();
      return;
    }

    inFlight.current = true;
    setOperation({ type: editing ? "update" : "create" });
    setDeleteTarget(null);
    setDeleteError("");
    setFieldErrors({});
    const entriesAtSubmit = entriesRef.current;
    try {
      const saved = editing ? await updateUpgradeLine(userId, editing.id, form) : await createUpgradeLine(userId, form);
      if (!mounted.current) return;
      if (!saved?.id || saved.userId !== userId || (editing && saved.id !== editing.id)) throw new Error("Invalid saved upgrade line");
      const current = entriesRef.current;
      if (editing) {
        // A committed live deletion must not be undone by a late update response.
        if (current && !current.some(row => row.id === editing.id)) {
          setSaveError(unavailableMessage);
          return;
        }
        // A newer committed snapshot stays authoritative over this response.
        if (current && current === entriesAtSubmit) showEntries(current.map(row => row.id === editing.id
          ? { ...row, ...saved, createdAt: row.createdAt || editing.createdAt } : row));
      } else {
        const existing = current?.find(row => row.id === saved.id);
        showEntries([{ ...existing, ...saved }, ...(current || []).filter(row => row.id !== saved.id)]);
      }
      resetForm();
      setSuccess(editing ? "Upgrade line updated." : "Upgrade line added.");
    } catch (failure) {
      if (mounted.current) {
        if (editing && failure.code === "UPGRADE_LINE_NOT_FOUND") {
          setEditing(current => current ? { ...current, unavailable: true } : current);
          setSaveError(unavailableMessage);
        } else setSaveError(saveErrorMessage);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setOperation(null);
    }
  };

  const requestDelete = entry => {
    if (inFlight.current) return;
    setDeleteTarget(entry.id);
    setDeleteError("");
    setSuccess("");
  };

  const confirmDelete = async entry => {
    if (inFlight.current || !userId || deleteTarget !== entry.id) return;
    if (!entriesRef.current?.some(row => row.id === entry.id)) return;
    inFlight.current = true;
    setOperation({ type: "delete", id: entry.id });
    setDeleteError("");
    setSuccess("");
    try {
      await deleteUpgradeLine(userId, entry.id);
      if (!mounted.current) return;
      if (entriesRef.current !== null) showEntries(entriesRef.current.filter(row => row.id !== entry.id));
      if (editing?.id === entry.id) resetForm();
      setDeleteTarget(null);
      setSuccess("Upgrade line deleted.");
    } catch (failure) {
      if (mounted.current) setDeleteError(failure.code === "UPGRADE_LINE_NOT_FOUND"
        ? "This entry is no longer available. Retry loading your saved entries." : deleteErrorMessage);
    } finally {
      inFlight.current = false;
      if (mounted.current) setOperation(null);
    }
  };

  const inputProps = name => ({
    id: `${fieldId}-${name}`,
    name,
    value: form[name],
    onChange: updateField,
    disabled: busy || !userId,
    "aria-invalid": fieldErrors[name] ? "true" : undefined,
    "aria-describedby": [name === "lastPaid" ? `${fieldId}-amount-help` : "", fieldErrors[name] ? `${fieldId}-${name}-error` : ""].filter(Boolean).join(" ") || undefined,
  });
  const fieldError = name => fieldErrors[name]
    ? <span className="upgrade-line-field-error" id={`${fieldId}-${name}-error`}>{fieldErrors[name]}</span>
    : null;

  return <section className={`upgrade-line-page${publicMode ? " upgrade-line-public" : ""}`} aria-labelledby={`${fieldId}-title`}>
    <header className="upgrade-line-heading">
      <h1 id={`${fieldId}-title`}>{tableOnly ? "Upgrade Line Management" : "Upgrade Line"}</h1>
      <p>{tableOnly ? "Review private submissions. Edit or delete entries below." : publicMode
        ? "Add a number and its payment details. Entries submitted here are shared with everyone who opens this page."
        : "Add a number and its payment details. Your saved entries appear below."}</p>
    </header>
    {(!tableOnly || editing) && <div className="upgrade-line-card">
      <h2>{editing ? "Edit upgrade line" : "Add upgrade line"}</h2>
      <form ref={formRef} className="upgrade-line-form" onSubmit={submit} noValidate>
        <div className="upgrade-line-fields">
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-number`}>Number <span aria-hidden="true">*</span></label>
            <input {...inputProps("number")} type="tel" autoComplete="tel" placeholder="Enter phone number" required />
            {fieldError("number")}
          </div>
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-noOfLines`}>No. of Lines <span aria-hidden="true">*</span></label>
            <input {...inputProps("noOfLines")} type="text" inputMode="numeric" placeholder="Enter number of lines" required />
            {fieldError("noOfLines")}
          </div>
          <div className="upgrade-line-field">
            <label htmlFor={`${fieldId}-lastPaid`}>Last Paid <span aria-hidden="true">*</span></label>
            <input {...inputProps("lastPaid")} type="text" inputMode="decimal" placeholder="0.00" required />
            <span className="upgrade-line-help" id={`${fieldId}-amount-help`}>Amount, up to 2 decimal places.</span>
            {fieldError("lastPaid")}
          </div>
          <div className="upgrade-line-field upgrade-line-remarks-field">
            <label htmlFor={`${fieldId}-remarks`}>Remarks <span className="upgrade-line-optional">(optional)</span></label>
            <textarea {...inputProps("remarks")} rows={3} maxLength={2000} placeholder="Add any notes" />
            {fieldError("remarks")}
          </div>
        </div>
        {Object.values(fieldErrors).some(Boolean) && <p className="upgrade-line-error" role="alert">Check the highlighted fields.</p>}
        {(saveError || editStatus) && <p className="upgrade-line-error" role="alert">{saveError || editStatus}</p>}
        {!userId && <p className="upgrade-line-error" role="alert">Sign in to add and view your upgrade lines.</p>}
        <div className="upgrade-line-form-footer">
          <button className="upgrade-line-primary" type="submit" disabled={busy || !userId || Boolean(editStatus)}>{operation?.type === "create" || operation?.type === "update" ? "Saving…" : editing ? "Save Changes" : "Add Upgrade Line"}</button>
          {editing && <button className="upgrade-line-secondary" type="button" disabled={busy} onClick={cancelEdit}>Cancel Edit</button>}
          <span className="upgrade-line-success" role="status">{success}</span>
        </div>
      </form>
    </div>}
    {tableOnly && success && <p className="upgrade-line-management-status upgrade-line-success" role="status">{success}</p>}
    <div className="upgrade-line-card upgrade-line-history" aria-labelledby={`${fieldId}-history-title`}>
      <div className="upgrade-line-history-heading">
        <h2 id={`${fieldId}-history-title`}>{tableOnly ? "Submitted entries" : "Saved entries"}</h2>
        {entries !== null && <span className="upgrade-line-count">{entries.length} {entries.length === 1 ? "entry" : "entries"}</span>}
      </div>
      {loadError && <div className="upgrade-line-load-error" role="alert"><p>{loadError}</p><button type="button" className="upgrade-line-retry" disabled={busy} onClick={() => setAttempt(value => value + 1)}>Retry</button></div>}
      <div className="upgrade-line-table-scroll" role="region" aria-label="Saved upgrade lines" tabIndex={0}>
        <table className="upgrade-line-table">
          <caption>{tableOnly ? "Private upgrade line submissions" : publicMode ? "Shared upgrade lines" : "Your saved upgrade lines"}</caption>
          <thead><tr><th scope="col">Number</th><th scope="col">No. of Lines</th><th scope="col">Last Paid</th><th scope="col">Remarks</th><th scope="col">Actions</th></tr></thead>
          <tbody>
            {entries?.map(entry => <tr key={entry.id}>
              <td className="upgrade-line-number">{entry.number}</td>
              <td>{entry.noOfLines}</td>
              <td className="upgrade-line-amount">{formatLastPaid(entry.lastPaidCents)}</td>
              <td className="upgrade-line-remarks">{entry.remarks || "—"}</td>
              <td className="upgrade-line-actions-cell">
                {deleteTarget === entry.id ? <div className="upgrade-line-delete-confirmation" role="group" aria-label={`Confirm deletion of ${entry.number}`}>
                  <p>Delete this entry?</p>
                  {deleteError && <p className="upgrade-line-delete-error" role="alert">{deleteError}</p>}
                  <div className="upgrade-line-row-actions">
                    <button className="upgrade-line-danger" type="button" disabled={busy} onClick={() => confirmDelete(entry)}>{operation?.type === "delete" && operation.id === entry.id ? "Deleting…" : "Delete Entry"}</button>
                    <button className="upgrade-line-secondary" type="button" disabled={busy} aria-label={`Cancel deletion of ${entry.number}`} onClick={() => { if (!inFlight.current) { setDeleteTarget(null); setDeleteError(""); } }}>Cancel</button>
                  </div>
                </div> : <div className="upgrade-line-row-actions">
                  <button className="upgrade-line-secondary" type="button" disabled={busy} aria-label={`Edit upgrade line ${entry.number}`} onClick={() => beginEdit(entry)}>Edit</button>
                  <button className="upgrade-line-delete" type="button" disabled={busy} aria-label={`Delete upgrade line ${entry.number}`} onClick={() => requestDelete(entry)}>Delete</button>
                </div>}
              </td>
            </tr>)}
            {entries === null && !loadError && userId && <tr><td className="upgrade-line-table-message" colSpan={5} role="status">{tableOnly ? "Loading submitted upgrade lines…" : publicMode ? "Loading shared upgrade lines…" : "Loading your upgrade lines…"}</td></tr>}
            {entries?.length === 0 && <tr><td className="upgrade-line-table-message" colSpan={5}>{tableOnly ? "No upgrade line submissions yet." : "No upgrade lines yet. Add your first entry above."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  </section>;
};

// Remount all form and history state whenever the data scope changes.
export const UpgradeLinePanel = ({ userId, publicMode = false, tableOnly = false }) => <UpgradeLineForm key={userId || "no-scope"} userId={userId} publicMode={publicMode} tableOnly={tableOnly} />;

const UpgradeLine = () => {
  const [userId, setUserId] = useState(undefined);
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (active) setUserId(user?.uid || null);
    }, () => { if (active) setUserId(null); });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
  if (userId === undefined) return <p className="upgrade-line-access-message" role="status">Checking administrator access…</p>;
  if (userId !== UPGRADE_LINE_ADMIN_UID) return <section className="upgrade-line-page">
    <header className="upgrade-line-heading"><h1>Upgrade Line Management</h1></header>
    <p className="upgrade-line-access-message" role="alert">Only the administrator can access these submissions.</p>
  </section>;
  return <UpgradeLinePanel key={userId} userId={PUBLIC_UPGRADE_LINE_SCOPE} tableOnly />;
};

export default UpgradeLine;
